'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useServiceHealth } from '@/hooks/use-service-health';
interface ServiceStatus {
  isConnected: boolean;
  error: string | null;
}

/**
 * Backend reachability, derived from the shared health singleton.
 *
 * This hook used to run its own 30s poll against /api/health and
 * /api/data-health while useServiceHealth ran a separate 60s poll against the
 * same two endpoints — 180 requests/hour each per open tab, and ~70% of all
 * Crucible API traffic. It now subscribes instead of fetching. Do not
 * reintroduce a fetch here; add to use-service-health.ts if the probe needs to
 * change.
 *
 * The return shape is unchanged. workspace-context gates `authReady` on
 * `isConnected`, which in turn gates session claim and every downstream
 * poller, so the contract matters more than it looks.
 */
export function useBackendConnection() {
  const $ = _c(18);
  const health = useServiceHealth();
  const t0 = health?.ai ?? false;
  const t1 = health?.aiError ?? null;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = {
      isConnected: t0,
      error: t1
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const ai = t2;
  const t3 = health?.data ?? false;
  const t4 = health?.dataError ?? null;
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = {
      isConnected: t3,
      error: t4
    };
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const data = t5;
  let errors;
  if ($[6] !== ai.isConnected || $[7] !== data || $[8] !== health) {
    errors = [];
    if (health && !ai.isConnected) {
      errors.push("AI service down");
    }
    if (health && !data.isConnected) {
      errors.push("Data service down");
    }
    $[6] = ai.isConnected;
    $[7] = data;
    $[8] = health;
    $[9] = errors;
  } else {
    errors = $[9];
  }
  const t6 = health === null;
  const t7 = errors.length > 0 ? errors.join(", ") : null;
  let t8;
  if ($[10] !== ai || $[11] !== data) {
    t8 = {
      ai,
      data
    };
    $[10] = ai;
    $[11] = data;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== ai.isConnected || $[14] !== t6 || $[15] !== t7 || $[16] !== t8) {
    t9 = {
      isConnected: ai.isConnected,
      isLoading: t6,
      error: t7,
      services: t8
    };
    $[13] = ai.isConnected;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}